import React from 'react';
import { LensePanel, type LenseItem } from '../../organisms/LensePanel';
import { Breadcrumb, type BreadcrumbItem } from '../../molecules/Breadcrumb';
import './HistoryPageLayout.css';

export interface HistoryPageLayoutProps {
  /** Category items for the selector panel */
  categories: LenseItem[];
  /** Currently selected category ID */
  selectedCategory?: string;
  /** Currently selected subcategory ID */
  selectedSubcategory?: string | null;
  /** Handler for category selection */
  onCategorySelect?: (categoryId: string, subcategoryId: string | null) => void;
  /** Root label for breadcrumb (e.g., "History") */
  rootLabel?: string;
  /** Optional class name */
  className?: string;
  /** Content (typically AuditLogTable) */
  children?: React.ReactNode;
}

/** Default categories for History section */
export const historyCategories: LenseItem[] = [
  { id: 'all', name: 'All Activity' },
  {
    id: 'documents',
    name: 'Document Activity',
    children: [
      { id: 'doc-uploads', name: 'Uploads' },
      { id: 'doc-downloads', name: 'Downloads' },
      { id: 'doc-edits', name: 'Edits' },
      { id: 'doc-deletions', name: 'Deletions' },
    ],
  },
  {
    id: 'data-changes',
    name: 'Data Changes',
    children: [
      { id: 'farm-data', name: 'Farm Data' },
      { id: 'livestock', name: 'Livestock Records' },
      { id: 'field-data', name: 'Field Data' },
    ],
  },
  {
    id: 'system',
    name: 'System Events',
    children: [
      { id: 'syncs', name: 'Sync Events' },
      { id: 'backups', name: 'Backups' },
      { id: 'integrations', name: 'Integrations' },
    ],
  },
  {
    id: 'user-activity',
    name: 'User Activity',
    children: [
      { id: 'logins', name: 'Logins' },
      { id: 'settings', name: 'Settings Changes' },
    ],
  },
  { id: 'submissions', name: 'Report Submissions' },
];

/**
 * HistoryPageLayout - Layout for the History section.
 * Features a selector panel with activity categories and an audit log content area.
 */
export const HistoryPageLayout: React.FC<HistoryPageLayoutProps> = ({
  categories = historyCategories,
  selectedCategory = 'all',
  selectedSubcategory = null,
  onCategorySelect,
  rootLabel = 'History',
  className = '',
  children,
}) => {
  // Build breadcrumb items from selection
  const buildBreadcrumbItems = (): BreadcrumbItem[] => {
    const items: BreadcrumbItem[] = [{ label: rootLabel }];

    const category = categories.find(c => c.id === selectedCategory);
    if (category) {
      // If subcategory is selected, make category clickable
      if (selectedSubcategory && category.children) {
        items.push({
          label: category.name,
          onClick: () => onCategorySelect?.(selectedCategory, null),
        });

        const subcategory = category.children.find(s => s.id === selectedSubcategory);
        if (subcategory) {
          items.push({ label: subcategory.name });
        }
      } else {
        items.push({ label: category.name });
      }
    }

    return items;
  };

  return (
    <div className={`history-page-layout ${className}`}>
      {/* Selector Panel */}
      <LensePanel
        items={categories}
        selectedItem={selectedCategory}
        selectedSubItem={selectedSubcategory}
        onSelect={onCategorySelect}
        className="history-page-layout__selector"
      />

      {/* Main Content Area */}
      <div className="history-page-layout__main">
        <header className="history-page-layout__header">
          <Breadcrumb items={buildBreadcrumbItems()} />
        </header>
        <div className="history-page-layout__content">
          {children}
        </div>
      </div>
    </div>
  );
};

export default HistoryPageLayout;
